const worker = new Worker('./js/worker.sql-wasm.js');

const Database = {
    TABLE: {
        CHNL: 'CHNL',
        SRV: 'SRV',
        SRV_DVB: 'SRV_DVB'
    },
    COLUMN: {
        CHANNEL_ID: 'chId',
        PLP_ID: 'plpId',
        TRANSPORT_ID: 'tsid',
        PROGRAM_NUMBER: 'progNum',
        SERVICE_ID: 'srvId',
        SERVICE_NAME: 'srvName',
        CHANNEL_TYPE: 'chType',
        SYMBOL_RATE: 'sr',
        FREQUENCY: 'freq',
        BANDWIDTH: 'band',
        MODULATION_TYPE: 'mod',
        ORIGINAL_NETWORK_ID: 'onid'
    }
};

const ChannelType = {
    ATV: 1,
    DTV: 2,
    CATV: 3,
    CDTV: 4,
    SDTV: 7
};

const DVBTypes = {
    DVB_T: 'dvbt',
    DVB_T2: 'dvbt2',
    DVB_C: 'dvbc',
    DVB_S: 'dvbs',
    DVB_S2: 'dvbs2'
};

const Bandwidth = {
    'UNKNOWN': -1,
    '5': 0,
    '6': 1,
    '7': 2,
    '8': 3,
    '10': 4,
    '1_7': 5
};
Bandwidth.MAX = Bandwidth['1_7'];

const SamsungModulationType = {
    'UNKNOWN': -1,
    'QAM16': 0,
    'QAM64': 1,
    'QAM128': 2,
    'QAM256': 3,
    'QPSK': 7,
    'VSB8': 4,
    'VSB16': 5,
    'NTSC': 6,
    'PAL': 8,
    'DMBT': 9,
    'OFDM': 10,
    'ISDB_T': 11,
    'ISDB_S': 12,
    'GB_S': 13,
    'GB_M': 14,
    'QAM32': 15,
    '8PSK': 16,
    '16APSK': 17,
    '32APSK': 18,
    'OFDM_T2': 19,
    'ATSC3': 20,
    'AUTO_QAM': 21
};
SamsungModulationType.MAX = SamsungModulationType.AUTO_QAM;

let finalChannels = [];

function exec(commands) {
    return new Promise(function (resolve, reject) {
        worker.addEventListener('message', resolve);
        worker.addEventListener('error', reject);
        worker.postMessage({ action: 'exec', sql: commands });
    });
}

export function open(file) {
    return new Promise(function (resolve, reject) {
        worker.addEventListener('message', resolve);
        worker.addEventListener('error', reject);
        worker.postMessage({ action: 'open', buffer: file }, [file]);
    });
}

/**
 * The SQLite lib we are using gives us two arrays, values and columns,
 * we use this method to convert the two arrays in to one channel object.
 *
 * @param {Array<String>} columns a array of database column names
 * @param {Array<Array<String|Number>>} values a array of database values
 * @returns {Array} a converted list of channel objects
 */
function columnsAndValuesToObject(columns, values) {
    let channelObjects = [];
    columns.forEach(function (column, colIndex) {
        values.forEach(function (values, valIndex) {
            if (!channelObjects[valIndex]) {
                channelObjects[valIndex] = {};
            }
            channelObjects[valIndex][column] = values[colIndex];
        });
    });

    return channelObjects;
}

/**
 * Service names are encoded with UTF-16BE, but coming out from SQLite as UTF-16LE.
 * To handle this, we need to swap the low and high bytes in each code point.
 *
 * 夀氀攀 吀嘀㄀ -> Yle TV1
 *
 * @param {String} serviceName is encoded name of the channel name
 * @returns {String} a decoded version of the channel name
 */
function decodeServiceName(serviceName) {
    let bytesArray = [];
    for (let i = 0; i < serviceName.length; ++i) {
        let code = serviceName.charCodeAt(i);
        bytesArray = bytesArray.concat([code & 0xFF, code / 256 >>> 0]);
    }

    return String.fromCharCode.apply(String, bytesArray);
}

/**
 * The Samsung clone does not provide detailed information about the DVB-T channel
 * so we need to figure it out our selves. It never provides a valid modulation for DVB-T,
 * we always have to assume its the highest in the modulation list for that type.
 *
 * The clone data can not provide if the channel is DVB-T or DVB-T2 it only knows it one of them.
 *
 * @param {Number} samsungModulation a number in the SamsungModulationType enum
 * @param {Number} plpId is ether 0xFFFF or 0
 * @returns {{modulation: String, type: String}}
 */
function getDVBTModulationAndType(samsungModulation, plpId) {
    let modulation = '64-QAM';
    let type = DVBTypes.DVB_T;
    if (samsungModulation === SamsungModulationType['OFDM_T2'] || plpId !== 0xFFFF) {
        type = DVBTypes.DVB_T2;
        modulation = '256-QAM';
    }

    return {
        modulation: modulation,
        type: type
    }
}

/**
 * Samsung clone should provide correct modulation values for DVB-C
 * but if it does not it will default to the highest value in the modulation list
 *
 * @param {Number} samsungModulation a number in the SamsungModulationType enum
 */
function getDVBCModulation(samsungModulation) {
    switch (samsungModulation) {
        case SamsungModulationType.QAM16:
            return '16-QAM';
        case SamsungModulationType.QAM32:
            return '32-QAM';
        case SamsungModulationType.QAM64:
            return '64-QAM';
        case SamsungModulationType.QAM128:
            return '128-QAM';
        case SamsungModulationType.QAM256:
        default:
            return '256-QAM';
    }
}

export async function onRawFileOpen() {
    let databaseQuery = 'SELECT ' + [
        Database.COLUMN.TRANSPORT_ID,
        Database.COLUMN.PLP_ID,
        Database.COLUMN.PROGRAM_NUMBER,
        Database.COLUMN.SERVICE_NAME,
        Database.TABLE.SRV_DVB + '.' + Database.COLUMN.ORIGINAL_NETWORK_ID,
        Database.TABLE.CHNL + '.' + Database.COLUMN.CHANNEL_TYPE,
        Database.TABLE.CHNL + '.' + Database.COLUMN.FREQUENCY,
        Database.TABLE.CHNL + '.' + Database.COLUMN.SYMBOL_RATE,
        Database.TABLE.CHNL + '.' + Database.COLUMN.BANDWIDTH,
        Database.TABLE.CHNL + '.' + Database.COLUMN.MODULATION_TYPE].join(',') + ' from ' + Database.TABLE.SRV +
        ' INNER JOIN ' + Database.TABLE.CHNL + ' ON ( ' + Database.TABLE.SRV + '.' + Database.COLUMN.CHANNEL_ID + ' = ' + Database.TABLE.CHNL + '.' + Database.COLUMN.CHANNEL_ID + ' )' +
        ' INNER JOIN ' + Database.TABLE.SRV_DVB + ' ON ( ' + Database.TABLE.SRV + '.' + Database.COLUMN.SERVICE_ID + ' = ' + Database.TABLE.SRV_DVB + '.' + Database.COLUMN.SERVICE_ID + ' )' +
        ' ORDER BY major, minor';

    try {
        return new Promise(function (resolve) {
            exec(databaseQuery).then(function (event) {
                if (event && event.data && event.data.results) {
                    const result = event.data.results[0];
    
                    const columnNames = Database.COLUMN;
                    const channels = columnsAndValuesToObject(result.columns, result.values);
                    finalChannels = channels.map((channel, index) => {
                        channel[columnNames.SERVICE_NAME] = decodeServiceName(channel[columnNames.SERVICE_NAME]);
    
                        switch (channel[columnNames.CHANNEL_TYPE]) {
                            case ChannelType.CATV:
                            case ChannelType.ATV:
                                channel[columnNames.CHANNEL_TYPE] = 'analog';
                                break;
                            case ChannelType.DTV:
                                const modulationAndType = getDVBTModulationAndType(channel[columnNames.MODULATION_TYPE], channel[columnNames.PLP_ID]);
                                channel[columnNames.CHANNEL_TYPE] = modulationAndType.type;
                                channel[columnNames.MODULATION_TYPE] = modulationAndType.modulation;
                                break;
                            case ChannelType.CDTV:
                                channel[columnNames.CHANNEL_TYPE] = DVBTypes.DVB_C;
                                channel[columnNames.MODULATION_TYPE] = getDVBCModulation(channel[columnNames.MODULATION_TYPE]);
                                break;
                            case ChannelType.SDTV:
                                // NOTE: this is not tested because we do not have any clones with DVB-S or DVB-S2 channels in them
                                channel[columnNames.CHANNEL_TYPE] = DVBTypes.DVB_S;
                                break;
                        }
    
                        switch (channel[columnNames.BANDWIDTH]) {
                            case Bandwidth['1_7']:
                                channel[columnNames.BANDWIDTH] = 1.7;
                                break;
                            case Bandwidth['5']:
                                channel[columnNames.BANDWIDTH] = 5;
                                break;
                            case Bandwidth['6']:
                                channel[columnNames.BANDWIDTH] = 6;
                                break;
                            case Bandwidth['7']:
                                channel[columnNames.BANDWIDTH] = 7;
                                break;
                            case Bandwidth['8']:
                                channel[columnNames.BANDWIDTH] = 8;
                                break;
                            case Bandwidth['10']:
                                channel[columnNames.BANDWIDTH] = 10;
                                break;
                            default:
                                channel[columnNames.BANDWIDTH] = 8;
                        }
    
                        const url = makeOldUrls(channel);
    
                        return {
                            number: index + 1,
                            name: channel[columnNames.SERVICE_NAME],
                            urimpg2: url,
                            internalnamesuffix: '',
                            type: 'TV',
                            epgid: channel[columnNames.PROGRAM_NUMBER]
                        }
                    });
                    resolve(finalChannels);
                }
            });
        });
        
    }
    catch (e) {
        console.log(e);
    }
}

function makeOldUrls(channel) {
    return 'dvb://' + [
        channel[Database.COLUMN.ORIGINAL_NETWORK_ID],
        channel[Database.COLUMN.TRANSPORT_ID],
        channel[Database.COLUMN.PROGRAM_NUMBER],
        channel[Database.COLUMN.FREQUENCY],
        channel[Database.COLUMN.BANDWIDTH],
        (channel[Database.COLUMN.SYMBOL_RATE] !== 0xFFFF ? channel[Database.COLUMN.SYMBOL_RATE] : 0),
        parseInt(channel[Database.COLUMN.MODULATION_TYPE])
    ].join('.') + '?type=' + channel[Database.COLUMN.CHANNEL_TYPE];
}
